// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { createFromProduct, ProductInfo } from "@insite/client-framework/Common/ProductInfo";
import { Dictionary } from "@insite/client-framework/Common/Types";
import waitFor from "@insite/client-framework/Common/Utilities/waitFor";
import { getIsWebCrawler } from "@insite/client-framework/Common/WebCrawler";
import {
    createHandlerChainRunner,
    executeAwaitableHandlerChain,
    Handler,
    HasOnError,
    HasOnSuccess,
    makeHandlerChainAwaitable,
} from "@insite/client-framework/HandlerCreator";
import {
    GetAlsoPurchasedProductCollectionApiV2Parameter,
    GetProductCollectionApiV2Parameter,
    GetRelatedProductCollectionApiV2Parameter,
} from "@insite/client-framework/Services/ProductServiceV2";
import loadRealTimeInventory from "@insite/client-framework/Store/CommonHandlers/LoadRealTimeInventory";
import loadRealTimePricing from "@insite/client-framework/Store/CommonHandlers/LoadRealTimePricing";
import loadProducts from "@insite/client-framework/Store/Data/Products/Handlers/LoadProducts";
import { ProductModel, RealTimePricingModel } from "@insite/client-framework/Types/ApiModels";
import { CustomAppState } from "@pacific/App/Store/Reducers";
import { getSettingsCollection } from "@pacific/App/Store/Selectors/SettingsSelectors";
import { levelDelimeter } from "@pacific/XDL/Utilities/helpers";
import cloneDeep from "lodash/cloneDeep";

type Parameter = (
    | GetProductCollectionApiV2Parameter
    | GetRelatedProductCollectionApiV2Parameter
    | GetAlsoPurchasedProductCollectionApiV2Parameter
) &
    HasOnSuccess<ProductModel[]> &
    HasOnError<string>;

type Props = {
    productInfosByProductId: Dictionary<ProductInfo>;
    pricingLoaded: boolean;
    products?: ProductModel[];
};

type HandlerType = Handler<Parameter, Props>;

export const LoadProducts: HandlerType = async (props: any) => {
    props.dispatch({
        type: "xProductList/TableView/SetIsLoadingLevelProducts",
        isLoadingLevelProducts: true,
    } as any);

    const state = props.getState() as CustomAppState;
    state.xProductList.previousProductsFilter?.attributeValueIds?.forEach(id => {
        if (props.parameter.attributeValueIds.indexOf(id) === -1) {
            props.parameter.attributeValueIds.push(id);
        }
    });

    const parameter = {
        ...state.xProductList.previousProductsFilter,
        ...props.parameter,
    };

    props.dispatch({
        type: "xProductList/TableView/SetProductsParameter",
        parameter,
        currentLevel: parameter.currentLevel,
    } as any);

    props.products = await executeAwaitableHandlerChain(loadProducts, parameter as any, props);
};

export const MapLevelProducts: HandlerType = props => {
    const state = props.getState() as CustomAppState;
    const { levels, levelsMap, currentLevel } = state.xProductList;
    const newLevelsMap = cloneDeep(levelsMap);
    const [level1, level2, level3] = levels;
    const [level1Key, level2Key] = currentLevel?.split(levelDelimeter) || [];
    if (!levels || levels.length === 0 || !currentLevel) {
        return;
    }
    const productsInfo: any = {};

    props.products?.forEach((product: ProductModel) => {
        if (!level2Key) {
            if (!newLevelsMap[level1Key]) {
                newLevelsMap[level1Key] = {
                    productsById: {},
                    valueId: level1.attributeTypeId,
                };
            }
            if (!newLevelsMap[level1Key].productsById) {
                newLevelsMap[level1Key]["productsById"] = {};
            }
            newLevelsMap[level1Key].productsById[product.id] = product;
            productsInfo[product.id] = {
                ...createFromProduct(product),
            };
            return;
        }
        if (!level3) {
            if (!newLevelsMap[level1Key][level2Key]) {
                newLevelsMap[level1Key][level2Key] = {
                    productsById: {},
                    valueId: level2.attributeTypeId,
                };
            }
            newLevelsMap[level1Key][level2Key].productsById[product.id] = product;
            productsInfo[product.id] = {
                ...createFromProduct(product),
            };
            return;
        }
        const productLevel3Attribute = product.attributeTypes?.find(o => o.id === level3.attributeTypeId);
        if (!productLevel3Attribute?.attributeValues) {
            return;
        }
        for (const value of productLevel3Attribute.attributeValues) {
            if (!newLevelsMap[level1Key][level2Key][`${level3.name}: ${value.value}`]) {
                newLevelsMap[level1Key][level2Key][`${level3.name}: ${value.value}`] = {
                    productsById: {},
                    valueId: level3.attributeTypeId,
                    sortOrder: value.sortOrder,
                };
            }
            newLevelsMap[level1Key][level2Key][`${level3.name}: ${value.value}`].productsById[product.id] = product;

            productsInfo[product.id] = {
                ...createFromProduct(product),
            };
        }
    });

    props.productInfosByProductId = productsInfo;

    props.dispatch({
        type: "xProductList/TableView/UpdateLevelsMap",
        levelsMap: newLevelsMap,
        productListTableProductsInfoById: productsInfo,
    } as any);

    props.dispatch({
        type: "xProductList/TableView/UpdateProductInfos",
        productInfosByProductId: productsInfo,
    } as any);
};

export const LoadRealTimePrices: HandlerType = async props => {
    const { productInfosByProductId } = props;
    const productIds = Object.keys(productInfosByProductId);
    if (productIds.length === 0) {
        return;
    }

    const loadRealTimePricingParameter: Parameters<typeof loadRealTimePricing>[0] = {
        productPriceParameters: productIds.map(o => productInfosByProductId[o]!),
        onSuccess: realTimePricing => {
            props.dispatch({
                type: "xProductList/TableView/CompleteLoadRealTimePricing",
                realTimePricing,
            } as any);
            props.pricingLoaded = true;
        },
        onError: () => {
            props.dispatch({
                type: "xProductList/TableView/FailedLoadRealTimePricing",
            } as any);
            props.pricingLoaded = true;
        },
        onComplete(realTimePricingProps) {
            if (realTimePricingProps.apiResult) {
                this.onSuccess?.(realTimePricingProps.apiResult);
            } else if (realTimePricingProps.error) {
                this.onError?.(realTimePricingProps.error);
            }
        },
    };

    const settings = getSettingsCollection(props.getState());

    if (IS_SERVER_SIDE && getIsWebCrawler() && !settings.websiteSettings.includeRealTimeInventoryAndPricingForSeo) {
        const awaitableLoadRealTimePricing = makeHandlerChainAwaitable<
            Parameters<typeof loadRealTimePricing>[0],
            RealTimePricingModel
        >(loadRealTimePricing);
        const realTimePricing = await awaitableLoadRealTimePricing({
            productPriceParameters: loadRealTimePricingParameter.productPriceParameters,
        })(props.dispatch, props.getState);
        if (realTimePricing) {
            loadRealTimePricingParameter.onSuccess?.(realTimePricing);
        } else {
            loadRealTimePricingParameter.onError?.(realTimePricing);
        }
    } else {
        props.dispatch(loadRealTimePricing(loadRealTimePricingParameter));
    }

    const productSettings = settings.productSettings;
    if (productSettings.canSeePrices && productSettings.inventoryIncludedWithPricing) {
        await waitFor(() => !!props.pricingLoaded);
    }
};

export const LoadRealTimeInventory: HandlerType = props => {
    if (
        !props.products?.length ||
        (getIsWebCrawler() &&
            !getSettingsCollection(props.getState()).websiteSettings.includeRealTimeInventoryAndPricingForSeo)
    ) {
        return;
    }

    props.dispatch(
        loadRealTimeInventory({
            productIds: props.products.map(o => o.id),
            onSuccess: realTimeInventory => {
                props.dispatch({
                    type: "xProductList/TableView/CompleteLoadRealTimeInventory",
                    realTimeInventory,
                } as any);
            },
            onError: () => {
                props.dispatch({
                    type: "xProductList/TableView/FailedLoadRealTimeInventory",
                } as any);
            },
            onComplete(realTimeInventoryProps) {
                if (realTimeInventoryProps.apiResult) {
                    this.onSuccess?.(realTimeInventoryProps.apiResult);
                } else if (realTimeInventoryProps.error) {
                    this.onError?.(realTimeInventoryProps.error);
                }
            },
        }),
    );

    props.dispatch({
        type: "xProductList/TableView/SetIsLoadingLevelProducts",
        isLoadingLevelProducts: false,
    } as any);
};

export const chain = [LoadProducts, MapLevelProducts, LoadRealTimePrices, LoadRealTimeInventory];

const loadProductsForTableView = createHandlerChainRunner(chain, "LoadProductsForTableView");
export default loadProductsForTableView;
